import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, type RouteLocationNormalizedLoaded, type RouteLocationRaw } from "vue-router";
import { useAbility } from "@/api/models/auth/ability";
import { ORGANIZATION_DETAIL_PREFIXES } from "@/composables/shared/useMyOrganization";
import { readBillingRights } from "@/composables/shared/billingAccess";
import { useLandingRoute } from "@/composables/shared/useLandingRoute";
import { isSafeInternalReturnPath } from "@/router/utils";

export const ADMINISTRATIVE_ROOT_PATH = "/administrative";

const PRACTICE_DETAIL_ROUTE_NAME = "/companies/[id].[[tab]]";

/** The practice (BE company) a detail route shows; edit and other company pages do not count. */
export function practiceDetailIdOf(route: RouteLocationNormalizedLoaded): string | undefined {
  if (route.name !== PRACTICE_DETAIL_ROUTE_NAME) return undefined;

  const { id } = route.params as { id?: string | string[] };
  return typeof id === "string" && id ? id : undefined;
}

export type NavbarItem = {
  title: string;
  icon: string;
  url?: string;
  to?: RouteLocationRaw;
  hidden?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  activePrefix?: string;
  extraActivePrefixes?: string[];
  activeMatch?: (path: string, returnTo?: string) => boolean;
  accentColor?: string;
  badgeNumber?: number;
  badgeColor?: string;
  dividerBefore?: boolean;
  dataTour?: string;
  children?: NavbarItem[];
};

/**
 * The navbar highlight should stay on the section the user came from, so `returnTo` (when it is a
 * safe internal path) wins over the current path's own prefix.
 */
export function matchesReturnToSection(returnTo: string | undefined, prefix: string): boolean {
  if (!returnTo || !isSafeInternalReturnPath(returnTo)) return false;

  const returnPath = returnTo.split("?")[0]?.split("#")[0] ?? "";

  return returnPath === prefix || returnPath.startsWith(`${prefix}/`);
}

export function isNavbarItemActive(item: NavbarItem, path: string, returnTo?: string): boolean {
  if (item.activeMatch) return item.activeMatch(path, returnTo);

  const prefixes = [item.url, item.activePrefix, ...(item.extraActivePrefixes ?? [])];

  return prefixes.some(
    (prefix) => prefix !== undefined && (path === prefix || path.startsWith(`${prefix}/`))
  );
}

export function useNavbarItems(openTutorialDialog: () => void) {
  const { t } = useI18n();

  // Section access comes from useLandingRoute so the navbar shows exactly the
  // sections the landing fallback considers reachable — one source of truth.
  const {
    canReadPatients,
    canAccessOfficesManagement,
    canAccessAdministration,
    canAccessPlatformControl,
    patientDashboardRoute,
  } = useLandingRoute();

  const route = useRoute();
  const { can } = useAbility();
  const billingRights = computed(() => readBillingRights(can));
  const canOpenBilling = computed(
    () => billingRights.value.canMsoBilling || billingRights.value.canCompanyBilling
  );

  const billingTo = computed<RouteLocationRaw | undefined>(() => {
    const practiceId = billingRights.value.canCompanyBilling
      ? practiceDetailIdOf(route)
      : undefined;
    return practiceId ? { path: "/billing", query: { company: practiceId } } : undefined;
  });

  const navItems = computed<NavbarItem[]>(() => [
    {
      title: t("generic.dashboard.title"),
      icon: "mdi-account-multiple",
      url: "/",
      to: patientDashboardRoute.value,
      badgeColor: "grey",
      hidden: !canReadPatients.value,
    },
    {
      title: t("generic.entity.organization"),
      icon: "mdi-hospital-building",
      url: "/organization",
      activeMatch: (path, returnTo) =>
        !matchesReturnToSection(returnTo, ADMINISTRATIVE_ROOT_PATH) &&
        ["/organization", ...ORGANIZATION_DETAIL_PREFIXES].some(
          (prefix) => path === prefix || path.startsWith(`${prefix}/`)
        ),
      hidden: !canAccessOfficesManagement.value,
    },
    {
      title: t("generic.navbar.paths.messages"),
      icon: "mdi-chat-outline",
      url: "/rooms",
      activePrefix: "/rooms",
      hidden: !canReadPatients.value,
    },
    {
      title: t("generic.navbar.paths.administrative"),
      icon: "mdi-cog",
      url: ADMINISTRATIVE_ROOT_PATH,
      activeMatch: (path, returnTo) =>
        path === ADMINISTRATIVE_ROOT_PATH ||
        path.startsWith(`${ADMINISTRATIVE_ROOT_PATH}/`) ||
        matchesReturnToSection(returnTo, ADMINISTRATIVE_ROOT_PATH),
      hidden: !canAccessAdministration.value,
    },
    {
      title: t("generic.fields.sections.billing"),
      icon: "mdi-credit-card-outline",
      url: "/billing",
      to: billingTo.value,
      hidden: !canOpenBilling.value,
    },
  ]);

  const menuItems = computed<NavbarItem[]>(() => [
    {
      title: t("generic.navbar.menuUser.adminSettings"),
      icon: "mdi-application-cog",
      url: "/admin-settings",
      disabled: !canAccessPlatformControl.value,
      hidden: !canAccessPlatformControl.value,
    },
    {
      title: t("generic.entity.settings"),
      icon: "mdi-account-cog",
      url: "/settings",
    },
  ]);

  const helpItems = computed<NavbarItem[]>(() => [
    {
      title: t("generic.navbar.menuUser.news"),
      icon: "mdi-newspaper",
      url: "/whats-new",
    },
    {
      title: t("generic.navbar.menuUser.help"),
      icon: "mdi-help-circle",
      onClick: openTutorialDialog,
    },
  ]);

  return {
    navItems,
    menuItems,
    helpItems,
  };
}
